import type { AnyAction } from 'redux';

import { QuoteStatus, QUOTE_STATUS } from '@frontend/api';
import type { QuoteItemQpvData } from '@frontend/api';

import { getActivePartUUID } from 'modules/viewerPage/data/reducer';

import { LOCATION_CHANGE } from 'store/app/types';
import type { RootState } from 'store/configureStore';
import { getGroupMemberHasQuoteEditPermissions } from 'store/quotes/selectors';

import { types } from './actions';

//re-exported for convenience
export {
  getActiveFileUUID,
  getActivePartUUID,
} from 'modules/viewerPage/data/reducer';

export type QuoteItem = QuoteItemQpvData & {
  next?: QuoteItem | null;
  previous?: QuoteItem | null;
};

export type QuoteItems = Map<number, QuoteItem>; //QuoteItem indexed by QuoteItem.id. (insertion order is by the QuoteItem.position)

export type QuotePartViewerState = {
  exportControlled?: boolean;
  isQuoteItemLocked: boolean;
  isQuotePartViewer?: boolean;
  quoteItemId?: number;
  quoteItems?: Map<number, QuoteItem>;
  quoteId?: number;
  quoteNum?: number;
  quoteRevision?: number;
  quoteStatus?: QuoteStatus;
  viewerEnabled: boolean;
};

const initialState = (): QuotePartViewerState => ({
  quoteItems: new Map(),
  viewerEnabled: true,
  isQuoteItemLocked: false,
});

const reducer = (
  state = initialState(),
  action: AnyAction,
): QuotePartViewerState => {
  const { payload, type } = action;
  switch (type) {
    case LOCATION_CHANGE:
      return initialState();
    case types.INIT_QUOTE_PART_VIEWER:
      return {
        ...state,
        quoteNum: payload.quoteNum,
        quoteRevision: payload.quoteRevision,
        quoteItemId: payload.quoteItemId,
        ...(!state.isQuotePartViewer && { isQuotePartViewer: true }),
      };
    case types.RECEIVE_QUOTE_ITEM_ID:
      return {
        ...state,
        quoteItemId: payload.quoteItemId,
        //Does not capture payload.partSetupState or payload.gotoLast.
        //partSetupState is stored in url query param and gotoLast is only used by saga
      };
    case types.RECEIVE_QUOTE_ITEMS:
      return {
        ...state,
        quoteItems: payload.quoteItems,
      };
    case types.RECEIVE_QUOTE_ITEM_UPDATE:
      return state.quoteItems?.size &&
        state.quoteItems.has(payload.quoteItem?.id)
        ? {
            ...state,
            quoteItems: new Map(
              (function* () {
                for (const quoteItem of state.quoteItems
                  ? state.quoteItems.values()
                  : []) {
                  const newQuoteItem =
                    quoteItem.id === payload.quoteItem?.id
                      ? { ...quoteItem, ...payload.quoteItem }
                      : quoteItem; //no change
                  yield [quoteItem.id, newQuoteItem];
                }
              })(),
            ),
          }
        : state;
    case types.RECEIVE_EXPORT_CONTROLLED:
      return {
        ...state,
        exportControlled: payload.exportControlled,
      };
    case types.RECEIVE_QUOTE_ID:
      return {
        ...state,
        quoteId: payload.id,
      };
    case types.RECEIVE_QUOTE_STATUS:
      return {
        ...state,
        quoteStatus: payload.quoteStatus,
      };
    case types.RESET:
      return initialState();
    case types.DISABLE_VIEWER:
      return {
        ...state,
        viewerEnabled: false,
      };
    case types.ENABLE_VIEWER:
      return {
        ...state,
        viewerEnabled: true,
      };
    case types.LOCK_QUOTE_ITEM:
      return {
        ...state,
        isQuoteItemLocked: true,
      };
    case types.TAKE_OVER_QUOTE_ITEM:
      return {
        ...state,
        isQuoteItemLocked: false,
      };
    default:
      return state;
  }
};

export default reducer;

const getScope = (state: RootState): QuotePartViewerState =>
  state.modules.quotePartViewer.app.quoteViewerPageReducer;

export const getIsQuotePartViewer = (state: RootState): boolean =>
  !!getScope(state).isQuotePartViewer;

export const getQuoteNum = (state: RootState) => getScope(state).quoteNum;

export const getQuoteRevision = (state: RootState) =>
  getScope(state).quoteRevision;

export const getQuoteNumberRevisionNumber = (state: RootState): string => {
  const { quoteNum, quoteRevision } = getScope(state);
  return `${quoteNum}${quoteRevision ? `-${quoteRevision}` : ''}`;
};

export const getQuoteItemId = (state: RootState): number | undefined =>
  getScope(state).quoteItemId;

export const getQuoteItems = (state: RootState) => getScope(state).quoteItems;

export const getQuoteItem = (state: RootState): QuoteItem | undefined => {
  const { quoteItemId, quoteItems } = getScope(state);
  const quoteItem = quoteItems?.get(quoteItemId ?? NaN);
  if (quoteItem) {
    return quoteItem;
  }
  const [firstQuoteItem] = quoteItems?.values() || [];
  return firstQuoteItem;
};

export const getQuoteItemPartUuids = (
  state: RootState,
): string[] | undefined => {
  const quoteItem = getQuoteItem(state);
  if (!quoteItem) {
    return undefined;
  }
  return quoteItem.components.map((component) => component.partUuid);
};

export const getComponentIdByPartUuid = (
  state: RootState,
  partUuid: string,
): number | undefined => {
  const quoteItem = getQuoteItem(state);
  if (!quoteItem) {
    return undefined;
  }
  return quoteItem.components.find(
    (component) => component.partUuid === partUuid,
  )?.id;
};

export const getActiveComponentId = (state: RootState): number | undefined => {
  const partUuid = getActivePartUUID(state);
  if (!partUuid) {
    return undefined;
  }
  return getComponentIdByPartUuid(state, partUuid);
};

export const getExportControlled = (state: RootState) =>
  getScope(state).exportControlled;

export const getQuoteId = (state: RootState): number | undefined =>
  getScope(state).quoteId;

export const getQuoteStatus = (state: RootState): QuoteStatus | undefined =>
  getScope(state).quoteStatus;

export const getIsViewerEnabled = (state: RootState): boolean =>
  getScope(state).viewerEnabled;

export const getIsQuoteItemLocked = (state: RootState): boolean =>
  getScope(state).isQuoteItemLocked;

export const getIsQuoteReadOnly = (state: RootState): boolean => {
  const hasPermissions = getGroupMemberHasQuoteEditPermissions(state);
  const quoteStatus = getQuoteStatus(state);
  return !hasPermissions || quoteStatus !== QUOTE_STATUS.DRAFT;
};
